import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { completeWelcome, updateName, useSession } from '../lib/session';
import Flag from '../components/Flag';

/**
 * Artboard 1c — shown once, after a first sign-in.
 * Two jobs: get a name that will read well under a photograph in the gallery,
 * and hand over the team. The team is treated as a moment rather than a form
 * field — it is the only purely delightful screen in the whole site.
 */
export default function Welcome() {
  const session = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState(session?.name ?? '');
  const [saving, setSaving] = useState(false);
  /**
   * Whether the guest has typed since the field was last in step with the
   * session. Profile carries the same ref for the same reason, and it belongs
   * here more than it does there, because on this screen the field is seeded
   * from a value that is *always* wrong on a first sign-in.
   *
   * `fromToken` commits synchronously with `cached?.name ?? 'Guest'` — there is
   * no `mv.profile` cache yet — and `loadProfile` replaces it with the name
   * Google gave a round trip later. The heading re-reads `session.name` on
   * every render so it corrects itself; a `useState` seed does not. That is how
   * a guest came to read **"You're in, Meg"** directly above a field saying
   * **"Guest"**.
   *
   * The field being wrong was the visible half. The damaging half is below: an
   * untouched field was still written on the way out, so tapping through put
   * the placeholder over the real name and walked every photograph the guest
   * had uploaded rewriting `uploaderName` (`setDisplayName`). Silently, with a
   * 200. It was invisible until #41 because `PATCH /me` had no grant and every
   * one of those writes failed.
   */
  const dirty = useRef(false);

  // Follow the session while the guest is not mid-edit. The `dirty` guard is
  // what stops a slow `/me` landing on top of what somebody is typing.
  useEffect(() => {
    if (!dirty.current) setName(session?.name ?? '');
  }, [session?.name]);

  if (!session) return null;

  /**
   * Save the name, then move on.
   *
   * Awaited rather than fired and forgotten, and this is the screen where that
   * matters most: before D55 this call wrote to `localStorage`, the next page
   * load overwrote it from the token, and the name a guest typed here was gone
   * — on the one screen whose entire purpose is collecting it.
   *
   * A failed write still lets them through. Standing at check-in on hotel wifi,
   * being held on a form is worse than being called by the name the identity
   * provider gave; Profile offers the same field for the rest of the week.
   */
  async function go() {
    setSaving(true);
    // `try`, because the sentence above is a promise and without this it is
    // only true by accident. `updateName` returns `{ ok: false }` rather than
    // throwing today, so nothing here would ever have caught — but if that
    // contract ever changes, an unguarded await leaves a guest stranded on this
    // screen with no way forward, standing at check-in. The cost of being sure
    // is one keyword.
    try {
      // Only what the guest actually typed. An untouched field holds the name
      // the identity provider already gave, so writing it back is a round trip
      // to store what is stored — and on the first render of a first sign-in it
      // is the "Guest" placeholder instead, which is the write that did the
      // damage. `updateName` short-circuits on an unchanged name; this is the
      // belt to that braces, because the value being equal is exactly what the
      // bug broke.
      if (dirty.current) await updateName(name);
    } catch {
      // Deliberately empty: `finally` does the work, and there is nothing to
      // tell a guest standing at check-in that they could act on. The `catch`
      // is not decoration — without it the rejection escapes `go()`, the call
      // site is `void go()` and attaches no handler, and the unhandled
      // rejection fails the whole suite while every assertion in it passes.
    } finally {
      completeWelcome();
      navigate('/now', { replace: true });
    }
  }

  return (
    <div className="auth">
      <div className="auth-col">
        <h2 style={{ margin: '0 0 4px' }}>You're in, {session.name.split(' ')[0]}</h2>
        <p style={{ fontSize: 13, opacity: 0.7, margin: '0 0 18px' }}>
          Signed in as <strong>{session.email}</strong> — use that address if you come back on another phone.
        </p>

        <div className="field" style={{ marginBottom: 20 }}>
          <label htmlFor="displayName">Your name, as it will show on your photos</label>
          <input
            id="displayName" className="input" style={{ minHeight: 50, fontSize: 16 }}
            value={name} autoComplete="name"
            onChange={(e) => { dirty.current = true; setName(e.target.value); }}
          />
        </div>

        <div className="reveal">
          {/* The flag is shown, not ghosted. The suit it replaces was a 230px
              glyph bleeding off the corner at 22% opacity, which worked because
              a ♠ is legible as a silhouette. A flag is read by its colours, so
              washing it out is the one thing that stops it being recognisable —
              and this screen exists to tell somebody which flag is theirs. */}
          <div className="reveal-blob" aria-hidden="true" />
          <div style={{ position: 'relative' }}>
            <Flag team={session.team} height={72} className="reveal-flag" decorative />
            <div className="reveal-kicker">Your team</div>
            <div className="reveal-name">{session.team}</div>
            <p>Teams play the welcome dinner game on Thursday. Nothing to do until then.</p>
          </div>
        </div>

        <button type="button" className="btn btn-primary btn-block" style={{ minHeight: 54, fontSize: 16, margin: '18px 0 16px' }} onClick={() => { void go(); }} disabled={saving}>
          {saving ? 'Saving…' : 'Take me to the schedule'}
        </button>
      </div>
    </div>
  );
}
